import * as React from "react";
import {
  makeStyles,
  Button,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  PeopleRegular,
  SignOutRegular,
  PanelLeftContractRegular,
  PanelLeftExpandRegular,
  SettingsRegular,
} from "@fluentui/react-icons";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../services/supabase";

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "center",
  },
  container: {
    display: "flex",
    width: "100%",
    maxWidth: "1200px",
  },
  sidebar: {
    width: "210px",
    backgroundColor: "#ffffff",
    borderRight: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    boxShadow: "2px 0 8px rgba(0, 0, 0, 0.03)",
    transitionProperty: "width",
    transitionDuration: "0.2s",
    transitionTimingFunction: "ease",
  },
  sidebarCollapsed: {
    width: "56px",
  },
  sidebarHeader: {
    padding: "12px 12px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "6px",
  },
  sidebarHeaderCollapsed: {
    padding: "12px 8px",
    flexDirection: "column",
    gap: "10px",
  },
  userSection: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "6px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    transitionProperty: "background-color",
    transitionDuration: "0.15s",
    flex: 1,
    minWidth: 0,
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  userSectionCollapsed: {
    padding: "6px",
    justifyContent: "center",
  },
  userDetails: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  hidden: {
    display: "none",
  },
  userName: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1a1a1a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
  },
  userEmail: {
    fontSize: "12px",
    color: "#666666",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    marginTop: "2px",
  },
  collapseButton: {
    minWidth: "32px",
    width: "32px",
    height: "32px",
    padding: "0",
    color: "#64748b",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "#f1f5f9",
      color: "#334155",
    },
  },
  nav: {
    flex: 1,
    padding: "12px 10px",
  },
  navCollapsed: {
    padding: "12px 6px",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    borderRadius: "8px",
    cursor: "pointer",
    color: "#64748b",
    textDecoration: "none",
    marginBottom: "4px",
    fontSize: "13px",
    fontWeight: "500",
    transitionProperty: "all",
    transitionDuration: "0.15s",
    transitionTimingFunction: "ease",
    "&:hover": {
      backgroundColor: "#f8fafc",
      color: "#334155",
    },
  },
  navItemCollapsed: {
    padding: "10px",
    justifyContent: "center",
  },
  navItemActive: {
    backgroundColor: "#ecfdf5",
    color: "#059669",
    fontWeight: "600",
    boxShadow: "0 1px 3px rgba(78, 203, 165, 0.1)",
  },
  navIcon: {
    flexShrink: 0,
    fontSize: "20px",
  },
  sidebarFooter: {
    padding: "10px 10px 14px 10px",
  },
  sidebarFooterCollapsed: {
    padding: "10px 6px 14px 6px",
  },
  logoContainer: {
    paddingTop: "12px",
    marginTop: "4px",
    borderTop: "1px solid #f1f5f9",
  },
  logoContainerCollapsed: {
    textAlign: "center",
  },
  logo: {
    fontSize: "18px",
    fontWeight: "700",
    background: "linear-gradient(135deg, #4ECBA5 0%, #2d8b6b 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    letterSpacing: "-0.5px",
  },
  logoCollapsed: {
    fontSize: "14px",
  },
  logoSubtext: {
    fontSize: "12px",
    color: "#94a3b8",
    fontWeight: "500",
    whiteSpace: "pre",
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  main: {
    flex: 1,
    padding: "24px",
    overflow: "auto",
    backgroundColor: "#f8fafc",
  },
});

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = React.useState<{
    email?: string;
    name?: string;
  } | null>(null);
  const [collapsed, setCollapsed] = React.useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  React.useEffect(() => {
    localStorage.setItem("sidebarCollapsed", String(collapsed));
  }, [collapsed]);

  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser({
          email: session.user.email,
          name:
            (session.user.user_metadata?.name as string) ||
            session.user.email,
        });
      }
    });
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  const navItems = [
    { path: "/users", label: "Users", icon: <PeopleRegular /> },
    { path: "/config", label: "Config", icon: <SettingsRegular /> },
  ];

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <aside
          className={styles.sidebar}
          style={{ width: collapsed ? 56 : 210, overflow: "hidden" }}
        >
          {/* User info */}
          <div
            className={`${styles.sidebarHeader} ${collapsed ? styles.sidebarHeaderCollapsed : ""}`}
          >
            {collapsed ? (
              <>
                <Tooltip content="Expand" relationship="label" positioning="after">
                  <Button
                    appearance="subtle"
                    icon={<PanelLeftExpandRegular />}
                    className={styles.collapseButton}
                    onClick={() => setCollapsed(false)}
                  />
                </Tooltip>
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Tooltip
                      content={user?.name || ""}
                      relationship="label"
                      positioning="after"
                    >
                      <div
                        className={`${styles.userSection} ${styles.userSectionCollapsed}`}
                      >
                        <Avatar
                          name={user?.name || user?.email}
                          size={32}
                          color="colorful"
                        />
                      </div>
                    </Tooltip>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem
                        icon={<SignOutRegular />}
                        onClick={handleLogout}
                      >
                        Sign out
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </>
            ) : (
              <>
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <div className={styles.userSection}>
                      <Avatar
                        name={user?.name || user?.email}
                        size={36}
                        color="colorful"
                      />
                      <div className={styles.userDetails}>
                        <Text className={styles.userName}>
                          {user?.name || user?.email?.split("@")[0]}
                        </Text>
                        <Text className={styles.userEmail}>
                          {user?.email}
                        </Text>
                      </div>
                    </div>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem
                        icon={<SignOutRegular />}
                        onClick={handleLogout}
                      >
                        Sign out
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
                <Tooltip content="Collapse" relationship="label">
                  <Button
                    appearance="subtle"
                    icon={<PanelLeftContractRegular />}
                    className={styles.collapseButton}
                    onClick={() => setCollapsed(true)}
                  />
                </Tooltip>
              </>
            )}
          </div>

          {/* Navigation */}
          <nav
            className={`${styles.nav} ${collapsed ? styles.navCollapsed : ""}`}
          >
            {navItems.map((item) => {
              const active = isActive(item.path);
              const content = (
                <div
                  key={item.path}
                  className={`${styles.navItem} ${collapsed ? styles.navItemCollapsed : ""} ${active ? styles.navItemActive : ""}`}
                  onClick={() => navigate(item.path)}
                >
                  <span className={styles.navIcon}>{item.icon}</span>
                  {!collapsed && <span>{item.label}</span>}
                </div>
              );
              return collapsed ? (
                <Tooltip
                  key={item.path}
                  content={item.label}
                  relationship="label"
                  positioning="after"
                >
                  {content}
                </Tooltip>
              ) : (
                content
              );
            })}
          </nav>

          {/* Logo */}
          <div
            className={`${styles.sidebarFooter} ${collapsed ? styles.sidebarFooterCollapsed : ""}`}
          >
            <div
              className={`${styles.logoContainer} ${collapsed ? styles.logoContainerCollapsed : ""}`}
            >
              {collapsed ? (
                <Text className={`${styles.logo} ${styles.logoCollapsed}`}>
                  T
                </Text>
              ) : (
                <Text className={styles.logo}>
                  Talkivo
                  <span className={styles.logoSubtext}>
                    {"  "}Admin
                  </span>
                </Text>
              )}
            </div>
          </div>
        </aside>

        <div className={styles.content}>
          <main className={styles.main}>{children}</main>
        </div>
      </div>
    </div>
  );
};
